// React and Redux imports
import React, { useMemo, useState, useCallback } from 'react'
import { useFormContext } from 'react-hook-form'
// Material-UI imports
import { Box } from '@mui/material'
import { styled } from '@mui/system'
// Custom components and helper imports
import BasicDataGrid from 'components/generic/datagrid'
import { TotalsRowCreateBulkInvoice } from '../misc/TotalsRowCreateBulkInvoice'
import { getOpenInvoicesColumns } from '../helpers/CreateBulkInvoiceHelpers'
import SingleInvoiceAdjustment from '../misc/SingleInvoiceAdjustment'
// Utility and type imports
import type { InvoicePaymentCreation } from 'components/containers/invoice-payments/types'
import useGetPricePrepend from 'utils/custom-hooks/useGetPricePrepend'
import { useAppSelector } from 'utils/custom-hooks/useTypedReduxStore'

interface CreateBulkInvoicePaymentTableProps {
  openInvoices: InvoicePaymentCreation[]
  onSelectionChange: (selectedIds: string[], isAllSelected: boolean) => void
  selectedInvoices: InvoicePaymentCreation[]
}

const GridStyling = styled('div')(() => ({
  '& .MuiDataGrid-root': {
    border: 'none',
    '& .MuiDataGrid-columnSeparator': {
      display: 'none',
    },
    '& .MuiDataGrid-cell:focus': {
      outline: 'none',
    },
    '& .MuiDataGrid-cell:focus-within': {
      outline: 'none',
    },
    '& .MuiDataGrid-columnHeader:focus': {
      outline: 'none',
    },
    '& .MuiDataGrid-cell': {
      paddingLeft: '16px',
      paddingRight: '16px',
    },
  },
}))

const CreateBulkInvoicePaymentTable: React.FC<CreateBulkInvoicePaymentTableProps> = ({
  openInvoices,
  onSelectionChange,
  selectedInvoices,
}) => {
  const { setValue, getValues, control } = useFormContext()
  const { defaultCurrency } = useAppSelector(state => state.settings)
  const [openAdjustment, setOpenAdjustment] = useState(false)
  const [currentInvoice, setCurrentInvoice] = useState<InvoicePaymentCreation | null>(null)
  const { pricePrepend } = useGetPricePrepend({ selectedCurrency: defaultCurrency })

  const handleOpenAdjustmentModal = useCallback(invoice => {
    setCurrentInvoice(invoice)
    setOpenAdjustment(true)
  }, [])

  const handleCloseAdjustmentModal = () => {
    setOpenAdjustment(false)
    setCurrentInvoice(null)
  }

  const handleSaveAdjustment = (adjustmentAmount: number, adjustmentNote: string) => {
    if (currentInvoice) {
      setValue(`invoices.${currentInvoice.id}.adjustment`, adjustmentAmount)
      setValue(`invoices.${currentInvoice.id}.adjustmentNote`, adjustmentNote)
    }
    handleCloseAdjustmentModal()
  }

  const handleSelectionChange = useCallback(
    (newSelectionModel: any[]) => {
      const isAllSelected = newSelectionModel.length === openInvoices.length
      onSelectionChange(newSelectionModel, isAllSelected)
    },
    [openInvoices, onSelectionChange],
  )

  const columns = useMemo(() => {
    return getOpenInvoicesColumns(
      pricePrepend,
      selectedInvoices?.map(invoice => invoice.id) || [],
      handleOpenAdjustmentModal,
      getValues,
      setValue,
      control,
    )
  }, [pricePrepend, selectedInvoices, handleOpenAdjustmentModal])

  return (
    <Box data-testid="invoice-payment-table-container" sx={{ mb: 3 }}>
      <Box flexGrow={1} overflow="auto">
        <GridStyling>
          <BasicDataGrid
            rows={openInvoices}
            columns={columns}
            disableSelectionOnClick
            autoHeight
            hideFooterPagination
            hideFooterSelectedRowCount
            checkboxSelection
            onSelectionModelChange={handleSelectionChange}
            selectionModel={selectedInvoices.map(invoice => invoice.id)}
            components={{
              Footer: TotalsRowCreateBulkInvoice,
            }}
            componentsProps={{
              checkbox: {
                onClick: () => {}, // this overwrites the native onClick function in the selection checkbox, which was causing a field focus bug
              },
              footer: {
                selectedInvoices,
              },
            }}
            initialState={{
              sorting: {
                sortModel: [{ field: 'invoiceTicketId', sort: 'asc' }],
              },
            }}
            disableColumnMenu
            data-testid="data-grid"
          />
        </GridStyling>
      </Box>
      <SingleInvoiceAdjustment
        open={openAdjustment}
        onClose={handleCloseAdjustmentModal}
        onSaveAdjustment={handleSaveAdjustment}
        initialAdjustmentAmount={currentInvoice?.adjustment || 0}
        data-testid="single-invoice-adjustment"
      />
    </Box>
  )
}

export default CreateBulkInvoicePaymentTable
